import React, { useState, useEffect, useMemo } from 'react';
import { getAuth, signOut } from './auth-compat';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './App.css';
import AddTaskModal from './AddTaskModal.js';
import { hasPermission, isSuperUser } from './utils/roleUtils';
import AddEtapaModal from './AddEtapaModal.js';
import SettingsModal from './SettingsModal.js';
import CommentsModal from './CommentsModal.js';
import DeadlineReminder from './components/DeadlineReminder.js';
import { useDeadlineReminder } from './hooks/useDeadlineReminder.js';
import { db, auth } from './firebase.js';
import { collection, onSnapshot, doc, addDoc, updateDoc, getDoc, deleteDoc, query, where, getDocs, orderBy, writeBatch } from './firestore-compat';
import { notifyNewTaskAssigned } from './utils/taskNotificationUtils.js';

// Funções auxiliares
const calcularStatusPrazo = (dataFim, status, dataConclusao) => {
    if (status === 'Feito') {
        if (dataConclusao && dataFim) {
            const dataConclusaoObj = new Date(dataConclusao + 'T00:00:00');
            const dataFimObj = new Date(dataFim + 'T00:00:00');
            if (dataConclusaoObj > dataFimObj) {
                const diffTime = dataConclusaoObj - dataFimObj;
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                return { texto: `Concluído (${diffDays}d após SLA)`, classe: 'prazo-atrasado' };
            } else {
                return { texto: 'Concluído no prazo', classe: 'prazo-concluido-ok' };
            }
        }
        return { texto: 'Concluído', classe: 'prazo-concluido-ok' };
    }
    if (!dataFim) {
        return { texto: '--', classe: 'prazo-ok' };
    }
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const dataFimObj = new Date(dataFim + 'T00:00:00');
    const diffTime = dataFimObj - hoje;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { texto: `Atrasado (${Math.abs(diffDays)}d)`, classe: 'prazo-atrasado' };
    if (diffDays === 0) return { texto: 'Termina hoje', classe: 'prazo-urgente' };
    return { texto: `Restam ${diffDays}d`, classe: 'prazo-ok' };
};

const calcularDatasDaEtapa = (tarefas) => {
    if (!tarefas || tarefas.length === 0) { return { inicio: null, fim: null }; }
    const tarefasComData = tarefas.filter(t => t && t.dataInicio && t.dataFim);
    if (tarefasComData.length === 0) { return { inicio: null, fim: null }; }
    const datasInicio = tarefasComData.map(t => new Date(t.dataInicio + 'T00:00:00'));
    const datasFim = tarefasComData.map(t => new Date(t.dataFim + 'T00:00:00'));
    const minDataInicio = new Date(Math.min.apply(null, datasInicio));
    const maxDataFim = new Date(Math.max.apply(null, datasFim));
    return { inicio: minDataInicio.toLocaleDateString(), fim: maxDataFim.toLocaleDateString() };
};

const isEtapaConcluida = (tarefas) => {
    if (!tarefas || tarefas.length === 0) return false;
    return tarefas.every(t => t.status === 'Feito');
};


function SortableEtapa({ etapa, expandedEtapaIds, toggleEtapa, children, ...props }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: etapa.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const etapaConcluida = isEtapaConcluida(etapa.tarefas);
    const datasEtapa = calcularDatasDaEtapa(etapa.tarefas);

    return (
        <div ref={setNodeRef} style={style}>
            <div className={`etapa-card ${etapaConcluida ? 'etapa-concluida' : ''}`}>
                <div className="etapa-info">
                    <span className="drag-handle" {...attributes} {...listeners}>&#x2630;</span>
                    <span className={`arrow ${expandedEtapaIds.includes(etapa.id) ? 'expanded' : ''}`} onClick={() => toggleEtapa(etapa.id)}>&#9654;</span>
                    <input type="text" className="etapa-title-input" style={{ fontWeight: 'bold' }} value={etapa.text} onChange={(e) => props.handleUpdateEtapa(etapa.id, e.target.value)} disabled={!props.canEditFields} />
                </div>
                <div className="etapa-datas">
                    {etapaConcluida ? <span className="status-concluido">Etapa Concluída</span> : datasEtapa.inicio && (<><span>Início: {datasEtapa.inicio}</span><span>Fim: {datasEtapa.fim}</span></>)}
                </div>
                {props.isAdmin && <button onClick={() => props.handleDeleteEtapa(etapa.id)} className="delete-etapa-btn">Apagar</button>}
            </div>
            {expandedEtapaIds.includes(etapa.id) && (
                <div className="tarefas-container">
                    {children}
                </div>
            )}
        </div>
    );
}


function ProjetoDirecao() {
    const [etapas, setEtapas] = useState([]);
    const [responsaveis, setResponsaveis] = useState([]);
    const [expandedEtapaIds, setExpandedEtapaIds] = useState([]);

    // Modals
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [isEtapaModalOpen, setIsEtapaModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);

    // User State
    const [userRole, setUserRole] = useState(null);
    const [userName, setUserName] = useState('');
    const [userPermissions, setUserPermissions] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const currentUser = auth.currentUser;

    const allTasksForReminder = useMemo(() => {
        return etapas.flatMap(etapa => etapa.tarefas || []);
    }, [etapas]);

    const { isTodayOpen, isWeekOpen, todayItems, weekItems, closeTodayPopup, closeWeekPopup, dismissToday, openReminderManually } = useDeadlineReminder(allTasksForReminder, 'projeto_direcao', 'task');

    // Filters
    const [filtroResponsavel, setFiltroResponsavel] = useState('todos');
    const [filtroStatus, setFiltroStatus] = useState([]);
    const [filtroSetor, setFiltroSetor] = useState('');
    const [filtroPrazo, setFiltroPrazo] = useState('todos');

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        const fetchUserData = async () => {
            if (currentUser && currentUser.email) {
                const usersRef = collection(db, 'users');
                const q = query(usersRef, where('email', '==', currentUser.email));
                const querySnapshot = await getDocs(q);

                if (!querySnapshot.empty) {
                    const userDoc = querySnapshot.docs[0];
                    const userData = userDoc.data();
                    // Normalizar: roles (array) ou role (string legada)
                    const role = Array.isArray(userData.roles) && userData.roles.length > 0
                        ? userData.roles[0] : (userData.role || 'leitor');
                    setUserRole(role);
                    setUserName(userData.name || currentUser.email);
                    setUserPermissions(userData.permissions || {});
                } else {
                    setUserRole('leitor');
                    setUserPermissions({});
                }
            }
            setIsLoading(false);
        };
        fetchUserData();

        // FIX: Usando nome da coleção compatível com as Regras de Segurança (etapas_direcao)
        // Antes estava 'etapas_direcao' que não tinha regra definida no novo arquivo.
        const COLLECTION_NAME = 'etapas_direcao';

        const unsubEtapas = onSnapshot(query(collection(db, COLLECTION_NAME), orderBy('order')), (snapshot) => {
            setEtapas(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (error) => console.error("Erro ao buscar etapas:", error));

        // Carrega responsáveis a partir dos usuários cadastrados no sistema
        const unsubResponsaveis = onSnapshot(query(collection(db, 'users'), orderBy('name')), (snapshot) => {
            setResponsaveis(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (error) => console.error("Erro ao buscar responsáveis:", error));

        return () => { unsubEtapas(); unsubResponsaveis(); };
    }, [currentUser]);

    // Permissões Centralizadas
    const isSuper = isSuperUser(currentUser?.email);
    const canView = isSuper || hasPermission(userRole, 'view_projeto_direcao', userPermissions);
    const canEdit = isSuper || hasPermission(userRole, 'edit_projeto_direcao', userPermissions);

    // Admin tem permissão total por padrão no hasPermission
    const isAdmin = canEdit; // Simplificação para manter lógica existente de visualização
    const canEditFields = canEdit;
    const canChangeStatusAndResponsible = canEdit || userRole === 'comentador';

    const handleAddEtapa = async (nomeNovaEtapa) => {
        const newOrder = etapas.length > 0 ? Math.max(...etapas.map(e => e.order || 0)) + 1 : 0;
        await addDoc(collection(db, 'etapas_direcao'), {
            text: nomeNovaEtapa,
            tarefas: [],
            order: newOrder
        });
    };

    function handleDragEnd(event) {
        const { active, over } = event;
        if (active.id !== over.id) {
            setEtapas((items) => {
                const oldIndex = items.findIndex(item => item.id === active.id);
                const newIndex = items.findIndex(item => item.id === over.id);
                const newArray = arrayMove(items, oldIndex, newIndex);
                const batch = writeBatch(db);
                newArray.forEach((etapa, index) => {
                    const etapaRef = doc(db, 'etapas_direcao', etapa.id);
                    batch.update(etapaRef, { order: index });
                });
                batch.commit();
                return newArray;
            });
        }
    }

    const toggleEtapa = (id) => {
        setExpandedEtapaIds(prev =>
            prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
        );
    };

    const handleAddTask = async (etapaId, newTask) => {
        const etapaRef = doc(db, 'etapas_direcao', etapaId);
        const etapaDoc = await getDoc(etapaRef);
        if (etapaDoc.exists()) {
            const tarefasAtuais = etapaDoc.data().tarefas || [];
            await updateDoc(etapaRef, { tarefas: [...tarefasAtuais, newTask] });
            
            // Disparar notificação se houver responsável
            if (newTask.responsavel) {
                notifyNewTaskAssigned(newTask.text, newTask.responsavel, userName, 'Projeto Direção');
            }
        }
    };

    const handleDuplicateTask = async (etapaId, tarefa) => {
        const newTask = {
            ...tarefa,
            id: Date.now(),
            status: 'Não iniciado',
            dataConclusao: null,
            comments: []
        };
        await handleAddTask(etapaId, newTask);
    };

    const handleDeleteTask = async (etapaId, tarefaId) => {
        const etapaRef = doc(db, 'etapas_direcao', etapaId);
        const etapaDoc = await getDoc(etapaRef);
        if (etapaDoc.exists()) {
            const tarefasAtuais = etapaDoc.data().tarefas || [];
            const tarefasAtualizadas = tarefasAtuais.filter(t => t && t.id !== tarefaId);
            await updateDoc(etapaRef, { tarefas: tarefasAtualizadas });
        }
    };

    const handleMoveTask = async (currentEtapaId, targetEtapaId, task) => {
        if (!targetEtapaId || currentEtapaId === targetEtapaId) return;

        // 1. Remover da etapa atual
        const currentEtapaRef = doc(db, 'etapas_direcao', currentEtapaId);
        const currentEtapaDoc = await getDoc(currentEtapaRef);
        if (currentEtapaDoc.exists()) {
            const tarefasAtuais = currentEtapaDoc.data().tarefas || [];
            await updateDoc(currentEtapaRef, { tarefas: tarefasAtuais.filter(t => t.id !== task.id) });
        }

        // 2. Adicionar na nova etapa
        const targetEtapaRef = doc(db, 'etapas_direcao', targetEtapaId);
        const targetEtapaDoc = await getDoc(targetEtapaRef);
        if (targetEtapaDoc.exists()) {
            const tarefasAlvo = targetEtapaDoc.data().tarefas || [];
            await updateDoc(targetEtapaRef, { tarefas: [...tarefasAlvo, task] });
        }
    };

    const handleUpdateTask = async (etapaId, tarefaId, campo, valor) => {
        const etapaRef = doc(db, 'etapas_direcao', etapaId);
        const etapaDoc = await getDoc(etapaRef);
        if (etapaDoc.exists()) {
            const tarefasAtuais = etapaDoc.data().tarefas || [];
            let tarefasAtualizadas = tarefasAtuais.map(t => {
                if (t && t.id === tarefaId) {
                    const tarefaModificada = { ...t, [campo]: valor };

                    if (campo === 'status') {
                        if (valor === 'Feito' && !t.dataConclusao) {
                            tarefaModificada.dataConclusao = new Date().toISOString().split('T')[0];
                        } else if (valor !== 'Feito') {
                            tarefaModificada.dataConclusao = null;
                        }
                    } else if (campo === 'responsavel' && t.responsavel !== valor && valor) {
                        notifyNewTaskAssigned(t.text || 'Tarefa', valor, userName, 'Projeto Direção');
                    }
                    return tarefaModificada;
                }
                return t;
            });
            await updateDoc(etapaRef, { tarefas: tarefasAtualizadas });
        }
    };

    const handleDeleteEtapa = async (etapaId) => {
        if (window.confirm("Tem certeza que deseja apagar esta etapa e todas as suas tarefas?")) {
            await deleteDoc(doc(db, 'etapas_direcao', etapaId));
        }
    };
    const handleUpdateEtapa = async (etapaId, novoNome) => {
        const etapaRef = doc(db, 'etapas_direcao', etapaId);
        await updateDoc(etapaRef, { text: novoNome });
    };
    const handleAddComment = async (etapaId, tarefaId, commentText) => {
        const etapaRef = doc(db, 'etapas_direcao', etapaId);
        const etapaDoc = await getDoc(etapaRef);
        if (etapaDoc.exists()) {
            const tarefasAtuais = etapaDoc.data().tarefas || [];
            const newComment = { text: commentText, userName: userName, userEmail: currentUser.email, timestamp: Date.now() };
            const tarefasAtualizadas = tarefasAtuais.map(t => {
                if (t && t.id === tarefaId) {
                    const comments = t.comments || [];
                    setSelectedTask(prevTask => ({ ...prevTask, comments: [...comments, newComment] }));
                    return { ...t, comments: [...comments, newComment] };
                }
                return t;
            });
            await updateDoc(etapaRef, { tarefas: tarefasAtualizadas });
        }
    };

    const openCommentsModal = (etapaId, task) => { setSelectedTask({ ...task, etapaId }); setIsCommentsModalOpen(true); };

    // --- LÓGICA DE FILTRAGEM ---
    const filteredEtapas = useMemo(() => {
        let etapasParaExibir = etapas;

        if (filtroResponsavel !== 'todos' || filtroStatus.length > 0 || filtroPrazo !== 'todos' || filtroSetor) {
            etapasParaExibir = etapas.map(etapa => {
                const tarefasFiltradas = (etapa.tarefas || []).filter(tarefa => {
                    if (!tarefa) return false;

                    const responsavelMatch = filtroResponsavel === 'todos' || tarefa.responsavel === filtroResponsavel;
                    const statusMatch = filtroStatus.length === 0 || filtroStatus.includes(tarefa.status);
                    const setorMatch = !filtroSetor || (tarefa.setor && tarefa.setor.toLowerCase().includes(filtroSetor.toLowerCase()));

                    const statusPrazo = calcularStatusPrazo(tarefa.dataFim, tarefa.status, tarefa.dataConclusao);
                    let prazoMatch = true;
                    if (filtroPrazo === 'atrasado') {
                        prazoMatch = statusPrazo.classe === 'prazo-atrasado';
                    } else if (filtroPrazo === 'urgente') {
                        prazoMatch = statusPrazo.classe === 'prazo-urgente';
                    } else if (filtroPrazo === 'no_prazo') {
                        prazoMatch = statusPrazo.classe === 'prazo-ok' || statusPrazo.classe === 'prazo-concluido-ok';
                    }

                    return responsavelMatch && statusMatch && prazoMatch && setorMatch;
                });
                return { ...etapa, tarefas: tarefasFiltradas };
            });
        }

        return etapasParaExibir;
    }, [etapas, filtroResponsavel, filtroStatus, filtroPrazo]);

    // --- AUTO-EXPAND ---
    useEffect(() => {
        if (filtroResponsavel !== 'todos' || filtroStatus.length > 0 || filtroPrazo !== 'todos' || filtroSetor) {
            const idsParaExpandir = filteredEtapas
                .filter(etapa => etapa.tarefas && etapa.tarefas.length > 0)
                .map(etapa => etapa.id);
            setExpandedEtapaIds(idsParaExpandir);
        }
    }, [filtroResponsavel, filtroStatus, filtroPrazo, filtroSetor, filteredEtapas]);


    if (isLoading) { return <div>Carregando permissões...</div>; }

    return (
        <div className="App">

            <div className="page-header">
                <div className="header-left">
                    <h2>Projeto Direção</h2>
                </div>
                <div className="header-actions">
                    <button className="reminder-btn" style={{ backgroundColor: '#e67e22', color: 'white', marginRight: '10px' }} onClick={openReminderManually}>
                        🔔 Próximos Vencimentos
                    </button>
                    {isAdmin && <button className="settings-btn" onClick={() => setIsSettingsModalOpen(true)} title="Configurações">&#9881;</button>}
                    {isAdmin && <button className="add-etapa-btn" onClick={() => setIsEtapaModalOpen(true)}>+ Adicionar Etapa</button>}
                    {isAdmin && <button className="add-task-btn" onClick={() => setIsTaskModalOpen(true)}>+ Adicionar Tarefa</button>}
                </div>
            </div>

            <div className="filter-bar-container">
                <div className="filter-group">
                    <label htmlFor="filtro-responsavel">Filtrar por Responsável:</label>
                    <select id="filtro-responsavel" value={filtroResponsavel} onChange={(e) => setFiltroResponsavel(e.target.value)}>
                        <option value="todos">Todos</option>
                        {responsaveis.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Filtrar por Status:</label>
                    <div style={{ display: 'flex', gap: '5px' }}>
                        {['Não iniciado', 'Em andamento', 'Feito'].map(status => (
                            <button
                                key={status}
                                onClick={() => {
                                    setFiltroStatus(prev => {
                                        if (prev.includes(status)) return prev.filter(s => s !== status);
                                        return [...prev, status];
                                    });
                                }}
                                style={{
                                    padding: '6px 12px',
                                    borderRadius: '20px',
                                    border: '1px solid #ccc',
                                    backgroundColor: filtroStatus.includes(status) ? '#007bff' : '#f8f9fa',
                                    color: filtroStatus.includes(status) ? '#fff' : '#333',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: filtroStatus.includes(status) ? 'bold' : 'normal',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {status}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="filter-group">
                    <label htmlFor="filtro-setor">Filtrar por Setor:</label>
                    <input
                        type="text"
                        id="filtro-setor"
                        value={filtroSetor}
                        onChange={(e) => setFiltroSetor(e.target.value)}
                        placeholder="Digite o setor..."
                        style={{
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            width: '160px'
                        }}
                    />
                </div>
                <div className="filter-group">
                    <label htmlFor="filtro-prazo">Filtrar por Prazo:</label>
                    <select id="filtro-prazo" value={filtroPrazo} onChange={(e) => setFiltroPrazo(e.target.value)}>
                        <option value="todos">Todos</option>
                        <option value="no_prazo">No Prazo</option>
                        <option value="urgente">Termina Hoje</option>
                        <option value="atrasado">Atrasado</option>
                    </select>
                </div>
                <button
                    className="clear-filters-btn"
                    onClick={() => {
                        setFiltroResponsavel('todos');
                        setFiltroStatus('todos');
                        setFiltroPrazo('todos');
                    }}
                >
                    Limpar Filtros
                </button>
            </div>

            {isAdmin && <SettingsModal isOpen={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} />}
            {isAdmin && <AddEtapaModal isOpen={isEtapaModalOpen} onClose={() => setIsEtapaModalOpen(false)} onAddEtapa={handleAddEtapa} />}
            {isAdmin && <AddTaskModal isOpen={isTaskModalOpen} onClose={() => setIsTaskModalOpen(false)} onAddTask={handleAddTask} etapas={etapas} responsaveis={responsaveis} />}

            <CommentsModal
                isOpen={isCommentsModalOpen}
                onClose={() => setIsCommentsModalOpen(false)}
                task={selectedTask}
                onAddComment={handleAddComment}
            />

            <main className="task-container">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={etapas.map(e => e.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {filteredEtapas.map(etapa => (
                            <SortableEtapa
                                key={etapa.id}
                                etapa={etapa}
                                expandedEtapaIds={expandedEtapaIds}
                                toggleEtapa={toggleEtapa}
                                handleUpdateEtapa={handleUpdateEtapa}
                                handleDeleteEtapa={handleDeleteEtapa}
                                canEditFields={canEditFields}
                                isAdmin={isAdmin}
                            >
                                {(etapa.tarefas && etapa.tarefas.length > 0) && (
                                    <div className="tarefa-header tarefa-header-direcao">
                                        <div className="header-tarefa-text">Tarefa</div>
                                        <div className="header-tarefa-setor">Setor</div>
                                        <div className="header-tarefa-responsavel">Responsável</div>
                                        <div className="header-tarefa-prioridade">Prioridade</div>
                                        <div className="header-tarefa-data">Início</div>
                                        <div className="header-tarefa-data">Fim</div>
                                        <div className="header-tarefa-data">Conclusão</div>
                                        <div className="header-tarefa-status">Status</div>
                                        <div className="header-tarefa-comment">Coment.</div>
                                        <div className="header-tarefa-calculado">Prazo</div>
                                        <div className="header-tarefa-move">Mover</div>
                                        <div className="header-tarefa-action">Ações</div>
                                    </div>
                                )}
                                {etapa.tarefas && etapa.tarefas
                                    .sort((a, b) => {
                                        const dataA = a.dataInicio;
                                        const dataB = b.dataInicio;

                                        if (!dataA && !dataB) return 0;
                                        if (!dataA) return 1;
                                        if (!dataB) return -1;

                                        return new Date(dataA) - new Date(dataB);
                                    })
                                    .map((tarefa, index) => {
                                        if (!tarefa) { return null; }
                                        const statusPrazo = calcularStatusPrazo(tarefa.dataFim, tarefa.status, tarefa.dataConclusao);
                                        const commentCount = tarefa.comments ? tarefa.comments.length : 0;
                                        return (
                                            <div key={tarefa.id || index} className="tarefa-card-detalhada tarefa-card-detalhada-direcao">
                                                <div className="tarefa-text">
                                                    <input type="text" value={tarefa.text || ''} onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'text', e.target.value)} className="text-input task-description-input" disabled={!canEditFields} />
                                                </div>
                                                <div className="tarefa-setor" data-label="Setor">
                                                    <input type="text" value={tarefa.setor || ''} onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'setor', e.target.value)} className="text-input" placeholder="Setor..." disabled={!canEditFields} />
                                                </div>
                                                <div className="tarefa-responsavel" data-label="Responsável">
                                                    <select value={tarefa.responsavel || ''} className="text-input" onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'responsavel', e.target.value)} disabled={!canChangeStatusAndResponsible}><option value="">--</option>{responsaveis.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}</select>
                                                </div>
                                                <div className="tarefa-prioridade" data-label="Prioridade">
                                                    {canEditFields ? (<select value={tarefa.prioridade || 'Média'} className={`prioridade-tag prioridade-${(tarefa.prioridade || 'média').toLowerCase()}`} onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'prioridade', e.target.value)}><option value="Baixa">Baixa</option><option value="Média">Média</option><option value="Alta">Alta</option><option value="Crítico">Crítico</option></select>) : (<span className={`prioridade-tag prioridade-tag-readonly prioridade-${(tarefa.prioridade || 'média').toLowerCase()}`}>{tarefa.prioridade || 'Média'}</span>)}
                                                </div>
                                                <div className="tarefa-data" data-label="Início">
                                                    <input type="date" value={tarefa.dataInicio || ''} onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'dataInicio', e.target.value)} className="date-input" disabled={!canEditFields} />
                                                </div>
                                                <div className="tarefa-data" data-label="Fim">
                                                    <input type="date" value={tarefa.dataFim || ''} onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'dataFim', e.target.value)} className="date-input" disabled={!canEditFields} />
                                                </div>
                                                <div className="tarefa-data" data-label="Conclusão">
                                                    {tarefa.status === 'Feito' ? (
                                                        <input type="date" value={tarefa.dataConclusao || ''} onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'dataConclusao', e.target.value)} className="date-input" disabled={!canEditFields} />
                                                    ) : (<span>--</span>)}
                                                </div>
                                                <div className="tarefa-status" data-label="Status">
                                                    <select value={tarefa.status || 'Não iniciado'} className={`status-select status-${(tarefa.status || 'nao-iniciado').replace(/\s+/g, '-').toLowerCase()}`} onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'status', e.target.value)} disabled={!canChangeStatusAndResponsible}><option value="Não iniciado">Não iniciado</option><option value="Em andamento">Em andamento</option><option value="Feito">Feito</option></select>
                                                </div>
                                                <div className="tarefa-comment">
                                                    <button className="comment-btn" onClick={() => openCommentsModal(etapa.id, tarefa)}>
                                                        💬 <span className="comment-count">{commentCount}</span>
                                                    </button>
                                                </div>
                                                <div className="tarefa-calculado" data-label="Prazo">
                                                    <span className={statusPrazo.classe}>{statusPrazo.texto}</span>
                                                </div>
                                                <div className="tarefa-move" data-label="Mover">
                                                    <select
                                                        className="move-select"
                                                        value={etapa.id}
                                                        onChange={(e) => handleMoveTask(etapa.id, e.target.value, tarefa)}
                                                        disabled={!canEditFields}
                                                        style={{ width: '100%', fontSize: '0.8rem', padding: '2px' }}
                                                    >
                                                        <option value={etapa.id}>Mover para...</option>
                                                        {etapas.filter(e => e.id !== etapa.id).map(e => (
                                                            <option key={e.id} value={e.id}>{e.text}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="tarefa-action" data-label="Ações">
                                                    <button onClick={() => handleDuplicateTask(etapa.id, tarefa)} className="duplicate-btn" title="Repetir (Duplicar)" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', marginRight: '5px' }}>🔄</button>
                                                    {isAdmin && <button onClick={() => handleDeleteTask(etapa.id, tarefa.id)} className="delete-btn" style={{ margin: 0 }}>🗑️</button>}
                                                </div>
                                            </div>
                                        );
                                    })}
                            </SortableEtapa>
                        ))}
                    </SortableContext>
                </DndContext>
            </main>
            <DeadlineReminder
                isTodayOpen={isTodayOpen}
                isWeekOpen={isWeekOpen}
                todayItems={todayItems}
                weekItems={weekItems}
                onCloseTodayPopup={closeTodayPopup}
                onCloseWeekPopup={closeWeekPopup}
                onDismissToday={dismissToday}
                type="task"
            />
        </div >
    );
}

export default ProjetoDirecao;
